// Phase 35 — Super-admin Platform Email Configuration page.
// Lives at /admin/email-config. Edits the singleton `platform_email_config`
// doc and exposes a "Send test email" button that hits the live Resend.
import { useEffect, useState } from "react";
import { platformApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Mail, Loader2, Send, CheckCircle2, AlertTriangle, Lock,
} from "lucide-react";

const DARK_CARD = "bg-slate-900 border border-slate-800 rounded-[8px] p-6 space-y-5";

export default function AdminEmailConfig() {
  const [cfg, setCfg] = useState(null);
  const [edits, setEdits] = useState({});      // local pending edits
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await platformApi.get("/platform/email-config");
      setCfg(data);
      setEdits({});
      setTestTo((t) => t || data?.from_email || "");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Read-helper: prefer pending edit, else current server value.
  const v = (k) => (edits[k] !== undefined ? edits[k] : cfg?.[k] ?? "");
  const set = (k, val) => setEdits((e) => ({ ...e, [k]: val }));

  const save = async () => {
    if (!Object.keys(edits).length) {
      toast.info("No changes to save");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...edits };
      // The API key field is plaintext-on-the-wire ONLY when the operator
      // typed something — blank means "keep current".
      if (payload.resend_api_key === "" || payload.resend_api_key === undefined) {
        delete payload.resend_api_key;
      }
      if (payload.custom_smtp_password === "" || payload.custom_smtp_password === undefined) {
        delete payload.custom_smtp_password;
      }
      const { data } = await platformApi.put("/platform/email-config", payload);
      setCfg(data);
      setEdits({});
      toast.success("Email configuration saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testTo || !testTo.includes("@")) {
      toast.error("Enter a valid recipient email");
      return;
    }
    setSending(true);
    try {
      const { data } = await platformApi.post(
        "/platform/email-config/test-send",
        { to_email: testTo },
      );
      if (data?.success) {
        toast.success(`Test sent to ${testTo}` + (data.message_id ? ` · id ${data.message_id.slice(0, 8)}…` : ""));
      } else {
        toast.error(`Send failed: ${data?.error || "unknown"}`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="text-slate-400 flex items-center gap-2" data-testid="admin-email-config-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-email-config-page">
      <div>
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-rose-400">// Platform Email Config</div>
        <h1 className="text-3xl font-black tracking-tight mt-2 text-slate-100 flex items-center gap-3">
          <Mail className="h-7 w-7 text-rose-400" /> Email configuration
        </h1>
        <p className="text-sm text-slate-400 mt-2 max-w-2xl">
          Edit the Resend (or custom-SMTP) credentials used for every outbound
          platform email — signup verification, invoice reminders, trial warnings,
          etc. Changes take effect immediately (no redeploy).
        </p>
      </div>

      {/* Identity */}
      <section className={DARK_CARD} data-testid="admin-email-config-identity">
        <h2 className="text-lg font-bold text-slate-100">Sender identity</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label className="text-slate-300">From email</Label>
            <Input
              type="email"
              value={v("from_email")}
              onChange={(e) => set("from_email", e.target.value)}
              className="bg-slate-950 border-slate-700 text-slate-100"
              placeholder="info@jobsbook.app"
              data-testid="admin-email-config-from-email"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-slate-300">From display name</Label>
            <Input
              value={v("from_display_name")}
              onChange={(e) => set("from_display_name", e.target.value)}
              className="bg-slate-950 border-slate-700 text-slate-100"
              placeholder="JobsBook"
              data-testid="admin-email-config-from-name"
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label className="text-slate-300">Reply-to email <span className="text-slate-500 text-xs">· optional</span></Label>
            <Input
              type="email"
              value={v("reply_to_email") || ""}
              onChange={(e) => set("reply_to_email", e.target.value || null)}
              className="bg-slate-950 border-slate-700 text-slate-100"
              placeholder="support@jobsbook.app"
              data-testid="admin-email-config-reply-to"
            />
          </div>
        </div>
      </section>

      {/* Resend API key */}
      <section className={DARK_CARD} data-testid="admin-email-config-resend">
        <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2">
          <Lock className="h-4 w-4 text-emerald-400" /> Resend API key
        </h2>
        <div className="space-y-1">
          <Label className="text-slate-300">Resend API key</Label>
          <Input
            type="password"
            value={edits.resend_api_key ?? ""}
            onChange={(e) => set("resend_api_key", e.target.value)}
            className="bg-slate-950 border-slate-700 text-slate-100 font-mono"
            placeholder={
              cfg?.resend_api_key_last_four
                ? `Currently set: …${cfg.resend_api_key_last_four} · Leave blank to keep`
                : "re_..."
            }
            data-testid="admin-email-config-resend-key"
          />
          <p className="text-[11px] text-slate-500">
            Stored encrypted at rest (Fernet). Only the last four characters are ever
            shown back to the UI. Leave the field blank to keep the current key.
          </p>
        </div>
      </section>

      {/* Custom SMTP */}
      <section className={DARK_CARD} data-testid="admin-email-config-smtp">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-slate-100">Custom SMTP</h2>
            <p className="text-[12px] text-slate-400 mt-1">
              Disable Resend and route emails through your own SMTP server instead.
            </p>
          </div>
          <Switch
            checked={!!v("custom_smtp_enabled")}
            onCheckedChange={(b) => set("custom_smtp_enabled", b)}
            data-testid="admin-email-config-smtp-toggle"
          />
        </div>
        {v("custom_smtp_enabled") && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-slate-800">
            <div className="space-y-1">
              <Label className="text-slate-300">SMTP host</Label>
              <Input
                value={v("custom_smtp_host") || ""}
                onChange={(e) => set("custom_smtp_host", e.target.value)}
                className="bg-slate-950 border-slate-700 text-slate-100"
                placeholder="smtp.example.com"
                data-testid="admin-email-config-smtp-host"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300">SMTP port</Label>
              <Input
                type="number"
                value={v("custom_smtp_port") || ""}
                onChange={(e) => set("custom_smtp_port", e.target.value ? Number(e.target.value) : null)}
                className="bg-slate-950 border-slate-700 text-slate-100"
                placeholder="587"
                data-testid="admin-email-config-smtp-port"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300">SMTP user</Label>
              <Input
                value={v("custom_smtp_user") || ""}
                onChange={(e) => set("custom_smtp_user", e.target.value)}
                className="bg-slate-950 border-slate-700 text-slate-100"
                data-testid="admin-email-config-smtp-user"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300">SMTP password</Label>
              <Input
                type="password"
                value={edits.custom_smtp_password ?? ""}
                onChange={(e) => set("custom_smtp_password", e.target.value)}
                className="bg-slate-950 border-slate-700 text-slate-100"
                placeholder={cfg?.custom_smtp_password_set ? "Leave blank to keep" : ""}
                data-testid="admin-email-config-smtp-password"
              />
            </div>
          </div>
        )}
      </section>

      {/* Save */}
      <div className="flex items-center gap-3">
        <Button
          onClick={save}
          disabled={saving || !Object.keys(edits).length}
          className="bg-rose-500 hover:bg-rose-400 text-white"
          data-testid="admin-email-config-save"
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
          Save configuration
        </Button>
        {Object.keys(edits).length > 0 && (
          <span className="text-[12px] mono text-amber-400">
            {Object.keys(edits).length} unsaved change{Object.keys(edits).length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {/* Test-send */}
      <section className={DARK_CARD} data-testid="admin-email-config-test">
        <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2">
          <Send className="h-4 w-4 text-sky-400" /> Send test email
        </h2>
        <p className="text-[12px] text-slate-400">
          Sends a small test email using the CURRENTLY SAVED configuration.
          The last result is stamped on the config doc and shown below.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
          <div className="flex-1 space-y-1">
            <Label className="text-slate-300">Recipient</Label>
            <Input
              type="email"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              className="bg-slate-950 border-slate-700 text-slate-100"
              placeholder="info@jobsbook.app"
              data-testid="admin-email-config-test-to"
            />
          </div>
          <Button
            onClick={sendTest}
            disabled={sending}
            className="bg-sky-500 hover:bg-sky-400 text-white"
            data-testid="admin-email-config-test-send"
          >
            {sending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
            Send test
          </Button>
        </div>
        {cfg?.last_test_send_at && (
          <div
            className={
              "mt-2 p-3 rounded-[6px] border text-[12px] " +
              (cfg.last_test_send_result === "success"
                ? "bg-emerald-950/40 border-emerald-900 text-emerald-200"
                : "bg-rose-950/40 border-rose-900 text-rose-200")
            }
            data-testid="admin-email-config-test-result"
          >
            <div className="flex items-center gap-2">
              {cfg.last_test_send_result === "success" ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <AlertTriangle className="h-4 w-4" />
              )}
              <span className="font-bold uppercase tracking-wider text-[11px]">
                {cfg.last_test_send_result}
              </span>
              <span className="text-slate-400 mono ml-auto">
                {cfg.last_test_send_at?.slice(0, 19).replace("T", " ")}
              </span>
            </div>
            <div className="mt-1">
              To: <span className="mono">{cfg.last_test_send_to}</span>
            </div>
            {cfg.last_test_send_error && (
              <div className="mt-1 mono text-[11px] opacity-90 break-words">
                {cfg.last_test_send_error}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
